/**
 * Live updates client (REALTIME.md §3–§5): one EventSource on `/api/events?v=2`, `id`-based
 * dedup, `Last-Event-ID` persisted in the `sync` store (folder `"*"`) so it survives reloads and
 * leader handovers, events handed to an `EventSink`, reconnect state machine, and cross-tab
 * fan-out through `LeaderElector`.
 *
 * `EventSource` cannot set headers, so when a *new* stream is opened with a known id the id is
 * also passed as `&lastEventId=` (the bridge treats it like the header). Native reconnects of
 * the same EventSource send the real `Last-Event-ID` header by themselves.
 */
import { LeaderElector, type SseFrame } from './leader';

export type ConnectionState = 'connected' | 'reconnecting' | 'offline' | 'syncing';

export interface SummaryMessage {
  from: { name: string; address: string } | { name: string; address: string }[];
  subject: string;
  preview?: string;
  date: string;
  receivedAt?: string | null;
  flags: string[];
  hasAttachments: boolean;
  size: number;
  messageId: string | null;
  inReplyTo: string | null;
  references: string[];
  toAddrs?: string[];
  list?: boolean;
  seq?: number;
}

export type LiveEvent = { id: number } & (
  /** `lastId` is the server's bus cursor at attach time, sent as data because a `hello` frame
   *  deliberately carries no `id:` line (it must never clobber Last-Event-ID). */
  | { type: 'hello'; user: string; serverTime?: string; resumed: boolean; replayed?: number; lastId?: number | null }
  | { type: 'message.new'; mailbox: string; uidValidity: string | number; uid: number; message: SummaryMessage }
  | { type: 'message.flags'; mailbox: string; uidValidity: string | number; uid: number; flags: string[]; modseq?: string | number }
  | { type: 'message.expunged'; mailbox: string; uidValidity: string | number; uids: number[] }
  | { type: 'mailbox.status'; mailbox: string; uidValidity: string | number; exists: number; unseen: number; uidNext: string | number; highestModseq?: string | number }
  | { type: 'mailbox.resync'; mailbox: string; reason: 'uidvalidity' | 'gap' | 'replay_overflow' | string }
  | { type: 'folders.changed' }
  /** The wire payload calls the outbox item `id`; it is renamed so it cannot collide with the event id. */
  | { type: 'outbox.state'; outboxId: string; state: 'queued' | 'sending' | 'sent' | 'failed' | 'cancelled'; sendAt?: number; error?: string }
  | { type: 'snooze.woke'; mailbox: string; uids: number[] }
  | { type: 'watcher.state'; mailbox?: string; state: 'idle' | 'reconnecting' | 'auth_error' | 'down'; error?: string }
  | { type: 'bye' }
);

export const LIVE_EVENT_TYPES: readonly string[] = Object.freeze([
  'hello',
  'message.new',
  'message.flags',
  'message.expunged',
  'mailbox.status',
  'mailbox.resync',
  'folders.changed',
  'outbox.state',
  'snooze.woke',
  'watcher.state',
  'bye',
]);

/** What the store implements to receive events. */
export interface EventSink {
  apply(ev: LiveEvent): void | Promise<void>;
  /** Run delta sync for the given folders (after reconnect or `hello.resumed=false`). */
  resync(folders: string[]): Promise<void>;
  currentFolder(): string;
}

/** Minimal EventSource surface, injectable for tests. */
export interface EventSourceLike {
  readonly readyState: number;
  onopen: ((ev: Event) => void) | null;
  onerror: ((ev: Event) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  addEventListener(type: string, listener: (ev: MessageEvent) => void): void;
  close(): void;
}
export type EventSourceCtor = new (url: string) => EventSourceLike;

export interface LiveClientOptions {
  sink: EventSink;
  url?: string;
  EventSourceImpl?: EventSourceCtor | null;
  loadLastEventId?: () => Promise<number | null>;
  saveLastEventId?: (id: number) => Promise<void>;
  elector?: LeaderElector | null;
  isOnline?: () => boolean;
  /** Manual reconnect backoff when the browser gives up (closed stream). */
  backoffMs?: readonly number[];
}

const DEFAULT_BACKOFF = Object.freeze([3000, 6000, 12000, 30000]);
const CLOSED = 2;

/** Parse one frame into a `LiveEvent` (null when the payload is not usable). */
export function parseFrame(frame: SseFrame): LiveEvent | null {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(frame.data) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const type = typeof data.type === 'string' ? data.type : frame.event;
  if (!type) return null;
  // The frame's `id:` line is authoritative; `data.id` is only an event id when numeric.
  const dataId = typeof data.id === 'number' ? data.id : NaN;
  const id = Number.isFinite(frame.id) && frame.id > 0 ? frame.id : Number.isFinite(dataId) ? dataId : 0;
  const ev = { ...data, id, type } as Record<string, unknown>;
  if (type === 'outbox.state') {
    // Wire payload: `{id: "<outbox uuid>", state}` — keep the item id under its own name.
    ev.outboxId = String(data.outboxId ?? (typeof data.id === 'string' ? data.id : ''));
  }
  return ev as LiveEvent;
}

export class LiveClient {
  state: ConnectionState = 'reconnecting';
  lastEventId = 0;
  /** Number of frames dropped as duplicates (diagnostics/tests). */
  duplicates = 0;
  private es: EventSourceLike | null = null;
  private readonly sink: EventSink;
  private readonly url: string;
  private readonly EventSourceImpl: EventSourceCtor | null;
  private readonly load: () => Promise<number | null>;
  private readonly save: (id: number) => Promise<void>;
  private readonly elector: LeaderElector | null;
  private readonly isOnline: () => boolean;
  private readonly backoff: readonly number[];
  private stateListeners = new Set<(s: ConnectionState) => void>();
  private hadError = false;
  private retries = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  /** Follower that opened its own stream because the leader looked frozen. */
  private takeover = false;
  private unsub: (() => void)[] = [];
  private readonly onOnline = () => {
    if (this.started && this.owns()) this.open(true);
  };
  private readonly onOffline = () => this.setState('offline');

  constructor(opts: LiveClientOptions) {
    this.sink = opts.sink;
    this.url = opts.url ?? '/api/events?v=2';
    this.EventSourceImpl = opts.EventSourceImpl === undefined ? (typeof EventSource !== 'undefined' ? (EventSource as unknown as EventSourceCtor) : null) : opts.EventSourceImpl;
    this.load = opts.loadLastEventId ?? (async () => null);
    this.save = opts.saveLastEventId ?? (async () => undefined);
    this.elector = opts.elector === undefined ? new LeaderElector() : opts.elector;
    this.isOnline = opts.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine));
    this.backoff = opts.backoffMs ?? DEFAULT_BACKOFF;
  }

  onState(cb: (s: ConnectionState) => void): () => void {
    this.stateListeners.add(cb);
    return () => this.stateListeners.delete(cb);
  }

  /** Load the persisted id, start election, open the stream when this tab owns it. */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    this.lastEventId = (await this.load()) ?? 0;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onOnline);
      window.addEventListener('offline', this.onOffline);
    }
    if (!this.elector) {
      this.open();
      return;
    }
    this.unsub.push(
      this.elector.onRole((role) => {
        if (role === 'leader' || role === 'solo') {
          this.takeover = false;
          this.open();
        } else if (role === 'follower') {
          this.closeStream();
          this.setState('connected');
        } else this.closeStream();
      }),
      this.elector.onFrame((frame) => {
        this.takeover = false; // the leader is alive again
        void this.handleFrame(frame);
      }),
      this.elector.onLeaderState((s) => {
        if (!this.takeover) this.setState(s as ConnectionState);
      }),
      this.elector.onStale(() => {
        if (!this.takeover) {
          this.takeover = true;
          this.open(true);
        }
      }),
    );
    this.elector.start();
  }

  stop(): void {
    this.started = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
      window.removeEventListener('offline', this.onOffline);
    }
    for (const u of this.unsub) u();
    this.unsub = [];
    this.elector?.stop();
    this.closeStream();
  }

  /** Whether this tab should hold the network stream right now. */
  private owns(): boolean {
    return !this.elector || this.elector.ownsStream || this.takeover;
  }

  private streamUrl(): string {
    return this.lastEventId > 0 ? `${this.url}${this.url.includes('?') ? '&' : '?'}lastEventId=${this.lastEventId}` : this.url;
  }

  /** Open (or re-open) the EventSource. */
  open(force = false): void {
    if (!this.started || !this.EventSourceImpl) return;
    if (this.es && !force) return;
    this.closeStream();
    if (!this.isOnline()) {
      this.setState('offline');
      return;
    }
    let es: EventSourceLike;
    try {
      es = new this.EventSourceImpl(this.streamUrl());
    } catch {
      this.setState('reconnecting');
      this.scheduleRetry();
      return;
    }
    this.es = es;
    es.onopen = () => {
      this.retries = 0;
      if (this.hadError) {
        this.hadError = false;
        // A `mailbox.resync` may have been missed while the stream was down.
        this.setState('syncing');
        void this.sink
          .resync(this.resyncFolders())
          .catch(() => undefined)
          .then(() => this.setState('connected'));
      } else this.setState('connected');
    };
    es.onerror = () => {
      this.hadError = true;
      this.setState(this.isOnline() ? 'reconnecting' : 'offline');
      // Native reconnect handles transient drops; a CLOSED stream (401, `bye`) needs our own timer.
      if (es.readyState === CLOSED && this.es === es) this.scheduleRetry();
    };
    const onFrame = (ev: MessageEvent) => {
      const id = Number(ev.lastEventId);
      void this.handleFrame({ id: Number.isFinite(id) ? id : 0, event: ev.type === 'message' ? '' : ev.type, data: String(ev.data ?? '') });
    };
    es.onmessage = onFrame;
    for (const type of LIVE_EVENT_TYPES) es.addEventListener(type, onFrame);
  }

  private closeStream(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (this.es) {
      try {
        this.es.close();
      } catch {
        /* ignore */
      }
      this.es = null;
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer || !this.started) return;
    const delay = this.backoff[Math.min(this.retries, this.backoff.length - 1)] ?? 30000;
    this.retries++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.started && this.owns()) this.open(true);
    }, delay);
  }

  private resyncFolders(): string[] {
    const cur = this.sink.currentFolder();
    return cur && cur.toUpperCase() !== 'INBOX' ? ['INBOX', cur] : ['INBOX'];
  }

  /**
   * Dedup by id, persist, fan out to followers, hand to the sink. Frames without an id
   * (legacy-style) are applied but never persisted.
   */
  async handleFrame(frame: SseFrame): Promise<void> {
    const ev = parseFrame(frame);
    if (!ev) return;
    const previousId = this.lastEventId;
    if (ev.id > 0) {
      if (ev.id <= previousId) {
        this.duplicates++;
        return;
      }
      this.lastEventId = ev.id;
      void this.save(ev.id).catch(() => undefined);
    }
    this.elector?.broadcastFrame({ id: ev.id, event: ev.type, data: frame.data });
    if (ev.type === 'hello') {
      this.setState('connected');
      // A fresh stream has no cursor of its own: adopt the bus position the server reports, so a
      // disconnect before the first live event can still resume instead of replaying blindly.
      const helloCursor = Number(ev.lastId);
      if (!this.lastEventId && Number.isFinite(helloCursor) && helloCursor > 0) {
        this.lastEventId = helloCursor;
        void this.save(helloCursor).catch(() => undefined);
      }
      if (!ev.resumed && previousId > 0) {
        // We had seen events before but the server could not replay the gap:
        // reconcile from the per-folder cursors instead.
        void this.sink.resync(this.resyncFolders()).catch(() => undefined);
      }
    } else if (ev.type === 'bye') {
      this.closeStream();
      this.scheduleRetry();
    }
    try {
      await this.sink.apply(ev);
    } catch {
      /* a failing handler must not stall the stream */
    }
  }

  private setState(s: ConnectionState): void {
    if (this.state === s) return;
    this.state = s;
    this.elector?.broadcastState(s);
    for (const cb of this.stateListeners) cb(s);
  }
}
